import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import DevicesIcon from "@mui/icons-material/Devices";

const BIOMETRIC_API_BASE =
  process.env.REACT_APP_BIOMETRIC_API_URL || "https://127.0.0.1:50801/api";

type FingerprintDevice = { id: string; name: string };

const isFingerprintImage = (value: string) =>
  value.includes("image/jpeg") || value.includes("image/jpg");

const SignatureBox = ({
  label,
  value,
  onChange,
  fingerprintDevice,
  detectingDevice,
  onDetectDevice,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  fingerprintDevice: FingerprintDevice | null;
  detectingDevice: boolean;
  onDetectDevice: () => void;
}) => {
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const [capturing, setCapturing] = useState(false);
  const fingerprint = Boolean(value) && isFingerprintImage(value);

  useEffect(() => {
    if (!value || fingerprint) return;
    const timer = window.setTimeout(() => {
      if (signatureRef.current && signatureRef.current.isEmpty()) {
        signatureRef.current.fromDataURL(value);
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [value, fingerprint]);

  const clearSignature = () => {
    signatureRef.current?.clear();
    onChange("");
  };

  const handleSignatureEnd = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      onChange("");
      return;
    }
    onChange(signatureRef.current.toDataURL());
  };

  const handleCaptureFingerprint = async () => {
    if (!fingerprintDevice) {
      toast.error("Please detect the fingerprint device first");
      return;
    }
    setCapturing(true);
    try {
      const response = await axios.get(`${BIOMETRIC_API_BASE}/ScanBiomini`, {
        timeout: 60000,
      });
      const retcode = Number(response?.data?.retcode);
      const imageData = response?.data?.image?.data;
      const imageFormat = response?.data?.image?.format || "jpg";
      if (retcode === 1 && imageData) {
        signatureRef.current?.clear();
        onChange(`data:image/${imageFormat};base64,${imageData}`);
        toast.success("Fingerprint captured successfully");
      } else {
        toast.error("Fingerprint capture failed. Please try again.");
      }
    } catch (error) {
      console.error("Fingerprint capture failed:", error);
      toast.error(
        "Unable to capture fingerprint. Ensure the device is connected."
      );
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: "13px", fontWeight: 700, mb: 1 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <Tooltip
          title={
            fingerprintDevice
              ? `Device Ready: ${fingerprintDevice.name}`
              : "Detect Device"
          }
        >
          <span>
            <IconButton
              size="small"
              color={fingerprintDevice ? "success" : "primary"}
              onClick={onDetectDevice}
              disabled={detectingDevice || capturing}
              sx={{
                border: "1px solid",
                borderColor: fingerprintDevice ? "success.main" : "primary.main",
                borderRadius: "4px",
                width: 28,
                height: 28,
              }}
            >
              {detectingDevice ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DevicesIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant="contained"
          startIcon={
            capturing ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <FingerprintIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={handleCaptureFingerprint}
          disabled={!fingerprintDevice || detectingDevice || capturing}
          sx={{ fontSize: "11px", textTransform: "none", minHeight: 28 }}
        >
          Capture Fingerprint
        </Button>
      </Box>
      <Box
        sx={{
          position: "relative",
          border: "1px solid #000",
          borderRadius: "4px",
          width: "100%",
          maxWidth: 280,
          height: 160,
          overflow: "hidden",
          background: "#fafafa",
        }}
      >
        {fingerprint && value ? (
          <Box
            component="img"
            src={value}
            alt={label}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            backgroundColor="transparent"
            canvasProps={{
              width: 280,
              height: 160,
              style: {
                width: "100%",
                height: "100%",
                display: "block",
                touchAction: "none",
                cursor: "crosshair",
              },
            }}
            onEnd={handleSignatureEnd}
            dotSize={1}
            minWidth={0.5}
            maxWidth={1.5}
          />
        )}
        {value && (
          <Tooltip title="Clear">
            <CancelIcon
              onClick={clearSignature}
              sx={{
                position: "absolute",
                top: 5,
                right: 5,
                background: "red",
                color: "#fff",
                cursor: "pointer",
                fontSize: "16px",
                borderRadius: "50%",
                padding: "2px",
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const DonorConsentDialog = ({
  open,
  onClose,
  donorSignature,
  doctorSignature,
  onDonorSignatureChange,
  onDoctorSignatureChange,
}: {
  open: boolean;
  onClose: () => void;
  donorSignature: string;
  doctorSignature: string;
  onDonorSignatureChange: (value: string) => void;
  onDoctorSignatureChange: (value: string) => void;
}) => {
  const [fingerprintDevice, setFingerprintDevice] =
    useState<FingerprintDevice | null>(null);
  const [detectingDevice, setDetectingDevice] = useState(false);

  const handleDetectFingerprintDevice = async (showErrorToast = true) => {
    setDetectingDevice(true);
    try {
      const response = await axios.get(`${BIOMETRIC_API_BASE}/devices`, {
        timeout: 10000,
      });
      const devices = response?.data?.Devices || [];
      const retcode = String(response?.data?.retcode ?? "");
      const fingerprintDeviceFound = devices.find(
        (device: { fingerprint?: string; id?: string; name?: string }) =>
          String(device?.fingerprint).toLowerCase() === "true"
      );
      if (retcode === "1" && fingerprintDeviceFound) {
        setFingerprintDevice({
          id: fingerprintDeviceFound.id,
          name: fingerprintDeviceFound.name || "BioMini",
        });
      } else {
        setFingerprintDevice(null);
        if (showErrorToast) {
          toast.error(
            "Fingerprint device not detected or configured. Please connect and configure the device."
          );
        }
      }
    } catch (error) {
      console.error("Fingerprint device detection failed:", error);
      setFingerprintDevice(null);
      if (showErrorToast) {
        toast.error(
          "Fingerprint device not detected or configured. Please connect and configure the device."
        );
      }
    } finally {
      setDetectingDevice(false);
    }
  };

  useEffect(() => {
    if (open) {
      handleDetectFingerprintDevice(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Donor Consent
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            className="urduNotationFont"
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              textAlign: "right",
              direction: "rtl",
              fontFamily: '"Noto Nastaliq Urdu", "NotoNastaliqUrdu", serif',
              lineHeight: 2.4,
              mb: 1.5,
            }}
          >
            خون عطیہ کرنے کی رضامندی
          </Typography>
          <Typography
            className="urduNotationFont"
            sx={{
              fontSize: "15px",
              textAlign: "right",
              direction: "rtl",
              fontFamily: '"Noto Nastaliq Urdu", "NotoNastaliqUrdu", serif',
              lineHeight: 2.4,
              mb: 1.5,
            }}
          >
            میں اپنی رضامندی اور مرضی سے خون عطیہ کر رہا/رہی ہوں اور تصدیق
            کرتا/کرتی ہوں کہ فراہم کردہ معلومات میرے علم کے مطابق درست ہیں۔
          </Typography>
          <Typography
            className="urduNotationFont"
            sx={{
              fontSize: "15px",
              textAlign: "right",
              direction: "rtl",
              fontFamily: '"Noto Nastaliq Urdu", "NotoNastaliqUrdu", serif',
              lineHeight: 2.4,
            }}
          >
            مجھے معلوم ہے کہ میرے خون کی ضروری بیماریوں کے لیے جانچ کی جائے گی
            اور میں خون عطیہ کرنے کے ممکنہ اثرات سے آگاہ ہوں۔
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SignatureBox
              label="Donor Signature/Thumb"
              value={donorSignature}
              onChange={onDonorSignatureChange}
              fingerprintDevice={fingerprintDevice}
              detectingDevice={detectingDevice}
              onDetectDevice={() => handleDetectFingerprintDevice(true)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SignatureBox
              label="Doctor Signature/Thumb"
              value={doctorSignature}
              onChange={onDoctorSignatureChange}
              fingerprintDevice={fingerprintDevice}
              detectingDevice={detectingDevice}
              onDetectDevice={() => handleDetectFingerprintDevice(true)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DonorConsentDialog;
